// Matches Tab Container
// Single-card vertical scroll view for Matches tab

import React, { useState, useEffect, useCallback, useRef } from "react";
import { FeedItem } from "../../../types/socialFeed";
import { swipeService } from "../../../services/Swipe/swipeService";
import { MatchCard } from "../FeedItems/MatchCard";
import { CompletenessBanner } from "./CompletenessBanner";
import { ProfilePeekDrawer, useProfilePeek } from "../../shared/ProfilePeekDrawer";
import { signalsService } from "../../../services/SocialFeed/signalsService";
import {
  MatchesContainer,
  MatchesScrollContainer,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  FeedToast,
} from "../../../styles/SocialFeed/MatchesTab.styles";

interface MatchesTabContainerProps {
  items: FeedItem[];
  userId: string;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  keepPileCount?: number;
  maxKeepPileSize?: number;
  /** Refetch the feed — used after the setup prompt changes the ranking data. */
  onRefresh?: () => void;
}

export const MatchesTabContainer: React.FC<MatchesTabContainerProps> = ({
  items,
  userId,
  isLoading,
  hasMore,
  onLoadMore,
  keepPileCount = 0,
  maxKeepPileSize = 5,
  onRefresh,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [deckCount, setDeckCount] = useState(0);
  const [usersInDeck, setUsersInDeck] = useState<Set<string>>(new Set());
  const [likedUsers, setLikedUsers] = useState<Set<string>>(new Set());
  // Who has been passed on this session. The pass is already persisted as a
  // swipe — the server excludes them from the next fetch — so this only has to
  // hold long enough for the card still on screen to show it.
  const [passedUsers, setPassedUsers] = useState<Set<string>>(new Set());
  // Shared with the other feeds; also emits profile_open and profile_dwell.
  const { peekTarget, openPeek, closePeek, toast, showToast } = useProfilePeek('matches');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [containerHeight, setContainerHeight] = useState(() => {
    // Initialize with full window height - the container will fill the parent
    // The navigation is fixed and overlays, so it doesn't affect the container height
    if (typeof window !== 'undefined') {
      return window.innerHeight;
    }
    return 0;
  });

  // Media comes from the backend: each candidate carries their own intro video
  // when they have uploaded one, and their main photo otherwise. This used to
  // substitute Google's BigBuckBunny samples for any item without media, which
  // put stock cartoons on real people's cards.
  const enhancedItems = items;


  // Calculate and update container height
  useEffect(() => {
    const updateHeight = () => {
      // Since MatchesContainer is now position: absolute with top: 0, bottom: 0,
      // it will automatically fill its parent. We don't need to calculate height.
      // But we'll still set it to window.innerHeight as a fallback for the $height prop.
      const windowHeight = window.innerHeight;
      setContainerHeight(windowHeight);
    };

    // Update on mount
    updateHeight();

    // Update on resize
    const handleResize = () => updateHeight();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load the deck once and derive membership locally. Depends on userId only:
  // the deck doesn't change because new feed items arrived, and re-running on
  // every `items` change would refetch on each page of infinite scroll.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    swipeService.getDeckProfileIds(userId)
      .then((ids) => {
        if (cancelled) return;
        setUsersInDeck(ids);
        setDeckCount(ids.size);
      })
      .catch((error) => console.error("Failed to load deck info:", error));

    return () => { cancelled = true; };
  }, [userId]);

  // Restore the like/pass buttons from the server, the same way the deck icon
  // above already restores itself. Without this the heart and X were session
  // state: a reload wiped them, and any profile that can legitimately come
  // back — one re-added to the deck queue, or opened directly — returned with
  // its buttons blank despite the swipe being on record.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    swipeService.getSwipedProfileIds()
      .then(({ liked, passed }) => {
        if (cancelled) return;
        // Seeded, not overwritten: anything swiped since mount is already in
        // these sets and must not be dropped by a slow response landing late.
        // (forEach rather than spreading Sets: this tsconfig targets ES5.)
        setLikedUsers((prev) => {
          const next = new Set(prev);
          liked.forEach((id) => next.add(id));
          return next;
        });
        setPassedUsers((prev) => {
          const next = new Set(prev);
          passed.forEach((id) => next.add(id));
          return next;
        });
      })
      .catch((error) => console.error("Failed to restore swipe state:", error));

    return () => { cancelled = true; };
  }, [userId]);


  // Handle like action
  // Drop someone from the deck locally after a swipe.
  //
  // No API call: recordSwipe already deletes the swipe_deck_queue row for the
  // profile it acted on, whatever the action was. Only this component's copy
  // of that state was left behind — which is why the card icon kept showing a
  // green tick for someone the server had already removed.
  const dropFromDeckLocally = useCallback((targetUserId: string) => {
    setUsersInDeck((prev) => {
      if (!prev.has(targetUserId)) return prev;
      const next = new Set(prev);
      next.delete(targetUserId);
      return next;
    });
  }, []);

  // The count follows the set, in one place, rather than being re-derived at
  // every call site that changes membership — which is how the two drift.
  useEffect(() => {
    setDeckCount(usersInDeck.size);
  }, [usersInDeck]);

  const handleLike = useCallback(
    async (targetUserId: string) => {
      try {
        // recordSwipe takes the *user* being swiped on, not the feed item id —
        // for candidate items those differ ("candidate:<uuid>" vs the uuid).
        await swipeService.recordSwipe(userId, targetUserId, 'like');
        // The heart is an up vote: interest in this profile.
        signalsService.track({ signalType: 'profile_like', targetUserId, surface: 'matches' });
        setLikedUsers((prev) => new Set(prev).add(targetUserId));
        // recordSwipe unqueues on ANY action, so a like leaves the deck just
        // as a pass does. Reflected here so the icon does not claim otherwise.
        dropFromDeckLocally(targetUserId);
        // Liking undoes a pass. The two are opposite answers to the same
        // question, and the swipes table agrees — record_swipe_and_check_match
        // upserts on (swiper_id, swiped_id), so the row holds the LATEST
        // choice, never both. The UI has to say the same thing: pass then
        // like used to leave a red X sitting next to a filled heart.
        setPassedUsers((prev) => {
          if (!prev.has(targetUserId)) return prev;
          const next = new Set(prev);
          next.delete(targetUserId);
          return next;
        });
      } catch (error: any) {
        console.error("Failed to like user:", error);
        if (error?.code === 'SWIPE_LIMIT_REACHED') showToast(error.message);
      }
    },
    [userId, showToast, dropFromDeckLocally]
  );

  // Handle pass action
  const handlePass = useCallback(
    async (targetUserId: string) => {
      try {
        await swipeService.recordSwipe(userId, targetUserId, 'pass');
        // The X is a down vote: an explicit loss of interest.
        signalsService.track({ signalType: 'profile_pass', targetUserId, surface: 'matches' });
        // Marked passed only AFTER the write succeeds, so the red X means the
        // swipe was actually recorded rather than merely clicked — the same
        // reason handleLike sets likedUsers here and not before the await.
        setPassedUsers((prev) => new Set(prev).add(targetUserId));
        // Passing on someone and keeping them queued in your deck are
        // contradictory answers, and the server has already unqueued them.
        dropFromDeckLocally(targetUserId);
        // Optionally remove from liked users if it was liked
        setLikedUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      } catch (error: any) {
        console.error("Failed to pass user:", error);
        if (error?.code === 'SWIPE_LIMIT_REACHED') showToast(error.message);
      }
    },
    [userId, showToast, dropFromDeckLocally]
  );

  // Handle adding/removing user from deck
  const handleSaveToDeck = useCallback(
    async (targetUserId: string, isCurrentlySaved: boolean) => {
      try {
        if (isCurrentlySaved) {
          await swipeService.removeFromDeck(userId, targetUserId);
          signalsService.track({ signalType: 'deck_remove', targetUserId, surface: 'matches' });
        } else {
          await swipeService.addToDeck(userId, targetUserId);
          // The card icon is the quick path to the same Add to Deck the
          // profile offers, so it records the same signal.
          signalsService.track({ signalType: 'deck_add', targetUserId, surface: 'matches' });
        }
        // Derive the count from the set rather than refetching the deck.
        const next = new Set(usersInDeck);
        if (isCurrentlySaved) next.delete(targetUserId);
        else next.add(targetUserId);
        setUsersInDeck(next);
        setDeckCount(next.size);
      } catch (error) {
        // Deck writes now throw when the server rejects them, so report the
        // failure and leave the button showing its real (unchanged) state.
        // Rethrowing here would only surface as an unhandled rejection —
        // MatchCard calls this without awaiting.
        console.error("Failed to toggle deck:", error);
        showToast("Couldn't update your deck. Please try again.");
      }
    },
    [userId, usersInDeck, showToast]
  );

  // Handle viewing profile — slides the profile in over the feed rather than
  // navigating away, so the user keeps their scroll position and can go
  // straight back to liking or passing.
  const handleViewProfile = useCallback(
    (profileId: string) => {
      const item = items.find((i) => i.userId === profileId);
      if (!item) return;
      openPeek({
        profileId,
        firstName: item.username || 'Someone',
        age: (item.metadata as any)?.age,
        compatibilityScore: item.content?.compatibilityScore,
        context: 'From your matches',
      });
    },
    [items, openPeek]
  );

  // Handle scroll to detect when to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Prevent division by zero
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;

      const scrollPercentage = scrollTop / maxScroll;

      // Calculate which card is currently visible based on scroll position
      // Each card is exactly clientHeight tall
      const cardHeight = clientHeight;
      const newIndex = Math.round(scrollTop / cardHeight);
      setCurrentCardIndex(newIndex);

      // Load more when near bottom (80% scrolled)
      if (scrollPercentage > 0.8 && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );


  if (isLoading && items.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', flex: 1, position: 'relative', minHeight: 0 }}>
        <MatchesContainer>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Loading matches...</p>
          </LoadingContainer>
        </MatchesContainer>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', flex: 1, position: 'relative', minHeight: 0 }}>
        <MatchesContainer>
          <EmptyState>
            <EmptyStateIcon>💕</EmptyStateIcon>
            <EmptyStateTitle>No matches yet</EmptyStateTitle>
            <EmptyStateDescription>
              Complete your profile and play Heart Games to see potential matches.
            </EmptyStateDescription>
            {/* An empty feed is most often an empty *profile* — show what to
                fill in rather than leaving the user at a dead end. */}
            <CompletenessBanner variant="inline" onProfileUpdated={onRefresh} />
          </EmptyState>
        </MatchesContainer>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', flex: 1, position: 'relative', minHeight: 0 }}>
      <MatchesContainer>
        {/* Onboarding prompt — hidden entirely once the profile is complete */}
        <CompletenessBanner onProfileUpdated={onRefresh} />

        {/* Scroll Container - One card at a time */}
        <MatchesScrollContainer
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {enhancedItems.map((item, index) => (
            <MatchCard
              key={item.id}
              item={item}
              onLike={handleLike}
              onPass={handlePass}
              onSaveToDeck={handleSaveToDeck}
              onViewProfile={handleViewProfile}
              isLiked={likedUsers.has(item.userId)}
              isSaved={usersInDeck.has(item.userId)}
              isPassed={passedUsers.has(item.userId)}
              // Only the card on screen plays its intro video; neighbours stay
              // preloaded so scrolling doesn't land on a stalled frame.
              isActive={index === currentCardIndex}
              shouldPreload={Math.abs(index - currentCardIndex) <= 1}
              surface="matches"
            />
          ))}

          {/* Loading More Indicator */}
          {isLoading && items.length > 0 && (
            <LoadingContainer>
              <LoadingSpinner />
              <p>Loading more matches...</p>
            </LoadingContainer>
          )}
        </MatchesScrollContainer>
      </MatchesContainer>

      {toast && <FeedToast role="status">{toast}</FeedToast>}

      {/* Profile preview — slides in over the feed so View never costs the
          user their place in the scroll. */}
      <ProfilePeekDrawer
        target={peekTarget}
        onClose={closePeek}
        backLabel="Matches"
        onNotify={(_type, message) => showToast(message)}
        actions={peekTarget ? [
          // No "Deck" action here: the profile body already carries its own
          // Add to Deck button, and two controls for one thing invites them to
          // disagree.
          {
            key: 'like',
            label: '♥ Like',
            variant: 'primary',
            onClick: () => handleLike(peekTarget.profileId),
          },
          {
            key: 'pass',
            label: '✕',
            variant: 'danger',
            ariaLabel: `Pass on ${peekTarget.firstName}`,
            onClick: () => handlePass(peekTarget.profileId),
          },
        ] : []}
      />
    </div>
  );
};

